// =====================================================================================
// INVRT Radar - web console - the operator consumption and price book screen (story O10)
//
// The W-D4 read-only operator screen over the two C3/C4 reads the api already serves
// (supabase/functions/api/routes/price_book.ts): GET /admin/price-book and
// GET /admin/organisations/:org/consumption. Nothing here writes: the screen calls GET
// endpoints only, and every authority decision stays on the server (requirePriceBookAdmin
// checks is_super_admin on the caller's own client; each RPC re-verifies
// admin_read_authorize in-database). This screen renders states, it never decides them.
//
// MONEY LAW (PRD section 2, restated in price_book.ts): price_book rows are immutable
// money FACTS and price_usd stays NULL until Mark rules the values, so a NULL price
// renders as "Not priced" - never 0, never blank, never invented. Metering is
// observability-only: nothing here blocks, caps or enforces anything.
//
// The consumption summary is per organisation by contract (the C4 read takes one org), so
// the section keeps the console's selector law: the operator chooses; with exactly one
// active organisation the choice that could not change anything is made outright.
// Responses are parsed fail-closed against the reviewed contracts, in the
// useOperatorOrganisations / catalogueReader idiom: a response that does not match throws
// the ApiError the states layer renders, and nothing is invented in the browser.
// =====================================================================================

import { useEffect, useMemo, useState } from "react";
import { Gauge } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Chip, type ChipSignal } from "@/components/ui/chip";
import { DataTable, type DataTableRow } from "@/components/ui/data-table";
import { MobileRows, type MobileRow } from "@/components/ui/mobile-rows";
import { Select } from "@/components/ui/input";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { formatCredits, formatDateTime } from "@/lib/format";
import { invalidContract, readCatalogueKind } from "@/pages/operator/catalogueReader";
import { useOperatorOrganisations } from "@/pages/operator/useOperatorOrganisations";

// ---- the reviewed C3/C4 contracts, parsed fail-closed ----------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const GEOGRAPHY_TIERS = ["city", "state", "multi_state", "national"] as const;
const OPERATIONS = ["search", "export", "delivery"] as const;
const BUNDLE_STATUSES = ["active", "exhausted", "cancelled"] as const;

type GeographyTier = (typeof GEOGRAPHY_TIERS)[number];
type Numeric = number | string;

interface PriceBookRow {
  id: string;
  campaign_id: string | null;
  geography_tier: GeographyTier;
  bundle_size: number;
  currency: string;
  price_usd: Numeric | null;
  active: boolean;
  created_at: string;
  created_by: string;
}

interface PriceBookPage {
  price_book: PriceBookRow[];
  next_cursor: string | null;
}

interface ConsumptionRow {
  campaign_id: string | null;
  total_quantity: Numeric;
  event_count: Numeric;
}

interface BundleBalanceRow {
  bundle_id: string;
  campaign_id: string | null;
  bundle_size: Numeric;
  consumed: Numeric;
  balance: Numeric;
  status: string;
  created_at: string;
}

interface ConsumptionSummary {
  org_id: string;
  from: string | null;
  to: string | null;
  by_operation: Array<{ operation: string; total_quantity: Numeric; event_count: Numeric }>;
  by_campaign: Array<ConsumptionRow>;
  bundle_balances: Array<BundleBalanceRow>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const received = Object.keys(value).sort();
  const expected = [...keys].sort();
  return received.length === expected.length && received.every((key, index) => key === expected[index]);
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isIsoDateTime(value: unknown): value is string {
  return typeof value === "string" && ISO_PATTERN.test(value) && !Number.isNaN(Date.parse(value));
}

function isNumeric(value: unknown): value is Numeric {
  return (typeof value === "number" && Number.isFinite(value)) ||
    (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value)));
}

function isPriceRow(value: unknown): value is PriceBookRow {
  if (!isRecord(value) || !hasExactKeys(value, ["id", "campaign_id", "geography_tier", "bundle_size", "currency", "price_usd", "active", "created_at", "created_by"])) {
    return false;
  }
  return isUuid(value.id) &&
    (value.campaign_id === null || isUuid(value.campaign_id)) &&
    (GEOGRAPHY_TIERS as readonly string[]).includes(String(value.geography_tier)) &&
    Number.isSafeInteger(value.bundle_size) && (value.bundle_size as number) > 0 &&
    typeof value.currency === "string" && value.currency.length >= 1 && value.currency.length <= 16 &&
    (value.price_usd === null || isNumeric(value.price_usd)) &&
    typeof value.active === "boolean" &&
    isIsoDateTime(value.created_at) &&
    isUuid(value.created_by);
}

function parsePriceBookPage(value: unknown): PriceBookPage | null {
  if (!isRecord(value) || !hasExactKeys(value, ["price_book", "next_cursor"]) || !Array.isArray(value.price_book)) {
    return null;
  }
  if (value.next_cursor !== null && typeof value.next_cursor !== "string") return null;
  return value.price_book.every(isPriceRow)
    ? { price_book: value.price_book as PriceBookRow[], next_cursor: value.next_cursor as string | null }
    : null;
}

/** The by_campaign rows of the C4 summary carry no operation field - the by_operation
 * rows above carry the operation split. */
function isConsumptionRow(value: unknown): value is ConsumptionRow {
  if (!isRecord(value) || !hasExactKeys(value, ["campaign_id", "total_quantity", "event_count"])) {
    return false;
  }
  return (value.campaign_id === null || isUuid(value.campaign_id)) &&
    isNumeric(value.total_quantity) &&
    isNumeric(value.event_count);
}

function isBundleBalanceRow(value: unknown): value is BundleBalanceRow {
  if (!isRecord(value) || !hasExactKeys(value, ["bundle_id", "campaign_id", "bundle_size", "consumed", "balance", "status", "created_at"])) {
    return false;
  }
  return isUuid(value.bundle_id) &&
    (value.campaign_id === null || isUuid(value.campaign_id)) &&
    isNumeric(value.bundle_size) && isNumeric(value.consumed) && isNumeric(value.balance) &&
    (BUNDLE_STATUSES as readonly string[]).includes(String(value.status)) &&
    isIsoDateTime(value.created_at);
}

function parseConsumptionSummary(value: unknown): ConsumptionSummary | null {
  if (!isRecord(value) || !hasExactKeys(value, ["consumption"])) return null;
  const summary = value.consumption;
  if (!isRecord(summary) || !hasExactKeys(summary, ["org_id", "from", "to", "by_operation", "by_campaign", "bundle_balances"])) {
    return null;
  }
  if (!isUuid(summary.org_id)) return null;
  if (summary.from !== null && !isIsoDateTime(summary.from)) return null;
  if (summary.to !== null && !isIsoDateTime(summary.to)) return null;
  if (!Array.isArray(summary.by_operation) || !Array.isArray(summary.by_campaign) || !Array.isArray(summary.bundle_balances)) {
    return null;
  }
  const byOperationValid = summary.by_operation.every((row): row is ConsumptionSummary["by_operation"][number] => {
    if (!isRecord(row) || !hasExactKeys(row, ["operation", "total_quantity", "event_count"])) return false;
    return (OPERATIONS as readonly string[]).includes(String(row.operation)) &&
      isNumeric(row.total_quantity) && isNumeric(row.event_count);
  });
  if (!byOperationValid) return null;
  if (!summary.by_campaign.every(isConsumptionRow)) return null;
  if (!summary.bundle_balances.every(isBundleBalanceRow)) return null;
  return summary as unknown as ConsumptionSummary;
}

// ---- display vocabulary -----------------------------------------------------------------

const OPERATION_LABELS: Record<string, string> = { search: "Search", export: "Export", delivery: "Delivery" };
const TIER_LABELS: Record<GeographyTier, string> = {
  city: "City",
  state: "State",
  multi_state: "Multi-state",
  national: "National",
};
const BUNDLE_STATUS_LABELS: Record<string, { label: string; signal: ChipSignal }> = {
  active: { label: "Active", signal: "buy" },
  exhausted: { label: "Exhausted", signal: "pace" },
  cancelled: { label: "Cancelled", signal: "risk" },
};

function operationLabel(operation: string): string {
  return OPERATION_LABELS[operation] ?? operation;
}

/** MONEY LAW: a NULL price is "Not priced" - never 0, never blank, never invented. */
function priceLabel(priceUsd: Numeric | null): string {
  return priceUsd === null ? "Not priced" : formatCredits(priceUsd);
}

function numberLabel(value: Numeric): string {
  return formatCredits(value);
}

// ---- the screen -------------------------------------------------------------------------

function OperatorDenial() {
  return (
    <EmptyState
      title="Consumption and the price book is for INVRT platform operators"
      body="This area is for INVRT platform operators. The server also refuses requests without super-admin access."
    />
  );
}

export function OperatorConsumptionScreen() {
  const { isSuperAdmin } = useSession();
  const api = useApiClient();
  const [orgId, setOrgId] = useState("");

  const organisations = useOperatorOrganisations({ enabled: isSuperAdmin });
  const activeOrganisations = useMemo(
    () => (organisations.data ?? []).filter((organisation) => organisation.status === "active"),
    [organisations.data],
  );

  // The console selector law: when exactly ONE organisation is active, choosing it could
  // not change anything, so it is selected outright; otherwise the operator chooses.
  useEffect(() => {
    if (!orgId && activeOrganisations.length === 1) setOrgId(activeOrganisations[0]!.id);
  }, [orgId, activeOrganisations]);

  // Campaign display names through the existing catalogue reader. A failed name read
  // falls back to the raw campaign id in the rows below; the ids stay truthful.
  const campaigns = useQuery({
    queryKey: ["operator", "consumption", "campaign-names"],
    queryFn: () => readCatalogueKind(api, "campaign"),
    enabled: isSuperAdmin,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const campaignNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const campaign of campaigns.data ?? []) names.set(campaign.id, campaign.name);
    return names;
  }, [campaigns.data]);

  const priceBook = useQuery({
    queryKey: ["operator", "consumption", "price-book"],
    queryFn: async (): Promise<PriceBookPage> => {
      const response = await api.get<unknown>("/admin/price-book", {
        requiresOrg: false,
        includeOrg: false,
        query: { limit: 100 },
      });
      const parsed = parsePriceBookPage(response);
      if (parsed === null) {
        throw invalidContract("The price book response did not match the reviewed C3 contract.");
      }
      return parsed;
    },
    enabled: isSuperAdmin,
    staleTime: 30_000,
  });

  const consumption = useQuery({
    queryKey: ["operator", "consumption", orgId],
    queryFn: async (): Promise<ConsumptionSummary> => {
      const response = await api.get<unknown>(`/admin/organisations/${orgId}/consumption`, {
        requiresOrg: false,
        includeOrg: false,
      });
      const parsed = parseConsumptionSummary(response);
      if (parsed === null) {
        throw invalidContract("The consumption response did not match the reviewed C4 contract.");
      }
      return parsed;
    },
    // No organisation chosen: nothing privileged leaves the browser for this section.
    enabled: isSuperAdmin && orgId !== "",
    staleTime: 30_000,
  });

  if (!isSuperAdmin) return <OperatorDenial />;

  const campaignLabel = (campaignId: string | null): string => {
    if (campaignId === null) return "Family-wide";
    return campaignNames.get(campaignId) ?? campaignId;
  };
  /** MONEY LAW wording: in the price book a NULL campaign_id is a family-level price,
   * the SQL comment's own term; the consumption tables use the metering wording. */
  const priceCampaignLabel = (campaignId: string | null): string => {
    if (campaignId === null) return "Family-level price";
    return campaignNames.get(campaignId) ?? campaignId;
  };
  const chosenOrganisation = activeOrganisations.find((organisation) => organisation.id === orgId);

  const priceRows: DataTableRow[] = (priceBook.data?.price_book ?? []).map((row) => ({
    id: row.id,
    campaign: priceCampaignLabel(row.campaign_id),
    tier: TIER_LABELS[row.geography_tier],
    usage: numberLabel(row.bundle_size),
    currency: row.currency,
    price: priceLabel(row.price_usd),
    status: (
      <Chip signal={row.active ? "buy" : "ctx"}>{row.active ? "Active" : "Superseded"}</Chip>
    ),
    created: formatDateTime(row.created_at),
  }));
  const priceMobileRows: MobileRow[] = (priceBook.data?.price_book ?? []).map((row) => ({
    id: row.id,
    name: priceCampaignLabel(row.campaign_id),
    value: priceLabel(row.price_usd),
    meta: `${TIER_LABELS[row.geography_tier]} · ${numberLabel(row.bundle_size)} · ${formatDateTime(row.created_at)}`,
    signal: row.active ? "buy" : "ctx",
    signalText: row.active ? "Active" : "Superseded",
  }));

  const summary = consumption.data;
  const operationRows: DataTableRow[] = (summary?.by_operation ?? []).map((row, index) => ({
    id: `${row.operation}:${index}`,
    operation: operationLabel(row.operation),
    quantity: numberLabel(row.total_quantity),
    events: numberLabel(row.event_count),
  }));
  const operationMobileRows: MobileRow[] = (summary?.by_operation ?? []).map((row, index) => ({
    id: `${row.operation}:${index}`,
    name: operationLabel(row.operation),
    value: numberLabel(row.total_quantity),
    meta: `${numberLabel(row.event_count)} events`,
  }));
  const campaignRows: DataTableRow[] = (summary?.by_campaign ?? []).map((row, index) => ({
    id: `${row.campaign_id ?? "family"}:${index}`,
    campaign: campaignLabel(row.campaign_id),
    quantity: numberLabel(row.total_quantity),
    events: numberLabel(row.event_count),
  }));
  const campaignMobileRows: MobileRow[] = (summary?.by_campaign ?? []).map((row, index) => ({
    id: `${row.campaign_id ?? "family"}:${index}`,
    name: campaignLabel(row.campaign_id),
    value: numberLabel(row.total_quantity),
    meta: `${numberLabel(row.event_count)} events`,
  }));
  const bundleRows: DataTableRow[] = (summary?.bundle_balances ?? []).map((row) => ({
    id: row.bundle_id,
    bundle: (
      <span className="font-mono text-xs" title={row.bundle_id}>{row.bundle_id.slice(0, 8)}</span>
    ),
    campaign: campaignLabel(row.campaign_id),
    size: numberLabel(row.bundle_size),
    consumed: numberLabel(row.consumed),
    balance: numberLabel(row.balance),
    status: (() => {
      const status = BUNDLE_STATUS_LABELS[row.status] ?? { label: row.status, signal: "ctx" as ChipSignal };
      return <Chip signal={status.signal}>{status.label}</Chip>;
    })(),
    created: formatDateTime(row.created_at),
  }));
  const bundleMobileRows: MobileRow[] = (summary?.bundle_balances ?? []).map((row) => {
    const status = BUNDLE_STATUS_LABELS[row.status] ?? { label: row.status, signal: "ctx" as ChipSignal };
    return {
      id: row.bundle_id,
      name: campaignLabel(row.campaign_id),
      value: numberLabel(row.balance),
      meta: `${numberLabel(row.consumed)} of ${numberLabel(row.bundle_size)} · ${formatDateTime(row.created_at)}`,
      signal: status.signal,
      signalText: status.label,
    };
  });

  return (
    <div className="flex flex-col gap-10">
      <header className="flex items-start gap-3">
        <Gauge className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-ink">Consumption</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-muted">
            What the estate consumes and how the price book stands. Metering is
            observability-only: nothing here blocks a product read, and a price the founder
            has not ruled renders as Not priced, never as zero.
          </p>
        </div>
      </header>

      {/* ---- consumption, per organisation (the C4 admin read) ---- */}
      <section className="flex flex-col gap-4" aria-labelledby="consumption-heading">
        <h2 id="consumption-heading" className="text-base font-semibold text-ink">
          Consumption by organisation
        </h2>
        {activeOrganisations.length > 0 ? (
          <Select
            label="Organisation"
            value={orgId}
            onChange={(event) => setOrgId(event.target.value)}
            className="h-11 lg:w-72"
            hint="The consumption summary below reads the chosen organisation."
          >
            <option value="">Select an organisation</option>
            {activeOrganisations.map((organisation) => (
              <option key={organisation.id} value={organisation.id}>
                {organisation.name}
              </option>
            ))}
          </Select>
        ) : organisations.isLoading ? (
          <LoadingState label="Reading organisations…" />
        ) : organisations.isError ? (
          <ErrorState error={organisations.error} onRetry={() => void organisations.refetch()} />
        ) : (
          <EmptyState
            title="No active organisations"
            body="Consumption is recorded per organisation, and none is active right now."
          />
        )}

        {orgId === "" ? (
          activeOrganisations.length > 0 ? (
            <EmptyState
              title="Choose an organisation"
              body="Pick an organisation above to read its consumption summary. The price book below is estate-wide."
            />
          ) : null
        ) : consumption.isLoading ? (
          <LoadingState label="Reading consumption…" />
        ) : consumption.isError ? (
          <ErrorState error={consumption.error} onRetry={() => void consumption.refetch()} />
        ) : summary ? (
          <div className="flex flex-col gap-6">
            <h3 className="text-sm font-semibold text-ink">{chosenOrganisation?.name ?? summary.org_id}</h3>
            <DataTable
              label="Consumption by operation"
              caption={`Operations metered for ${chosenOrganisation?.name ?? summary.org_id}`}
              columns={[
                { key: "operation", label: "Operation" },
                { key: "quantity", label: "Total quantity", numeric: true },
                { key: "events", label: "Events", numeric: true },
              ]}
              rows={operationRows}
              empty={
                <EmptyState
                  compact
                  title="Nothing metered yet"
                  body="No searches, exports or deliveries are recorded for this organisation."
                />
              }
            />
            <MobileRows rows={operationMobileRows} label="Consumption by operation mobile view" className="md:hidden" />
            <DataTable
              label="Consumption by campaign"
              columns={[
                { key: "campaign", label: "Campaign" },
                { key: "quantity", label: "Total quantity", numeric: true },
                { key: "events", label: "Events", numeric: true },
              ]}
              rows={campaignRows}
              empty={
                <EmptyState
                  compact
                  title="No campaign metering"
                  body="No consumption is recorded against a campaign for this organisation."
                />
              }
            />
            <MobileRows rows={campaignMobileRows} label="Consumption by campaign mobile view" className="md:hidden" />
            <DataTable
              label="Bundle balances"
              columns={[
                { key: "bundle", label: "Bundle" },
                { key: "campaign", label: "Campaign" },
                { key: "size", label: "Size", numeric: true },
                { key: "consumed", label: "Consumed", numeric: true },
                { key: "balance", label: "Balance", numeric: true },
                { key: "status", label: "Status" },
                { key: "created", label: "Created" },
              ]}
              rows={bundleRows}
              empty={
                <EmptyState
                  compact
                  title="No bundles granted"
                  body="Granted bundles and their balances appear here once operators grant them."
                />
              }
            />
            <MobileRows rows={bundleMobileRows} label="Bundle balances mobile view" className="md:hidden" />
          </div>
        ) : null}
      </section>

      {/* ---- the price book, estate-wide (the C3 admin read) ---- */}
      <section className="flex flex-col gap-4" aria-labelledby="price-book-heading">
        <h2 id="price-book-heading" className="text-base font-semibold text-ink">
          Price book
        </h2>
        {priceBook.isLoading ? (
          <LoadingState label="Reading the price book…" />
        ) : priceBook.isError ? (
          <ErrorState error={priceBook.error} onRetry={() => void priceBook.refetch()} />
        ) : (
          <div className="flex flex-col gap-2">
            {priceBook.data?.next_cursor ? (
              <p className="text-xs text-ink-muted">
                Showing the most recent 100 price book rows; older rows keep loading behind
                the same cursor read.
              </p>
            ) : null}
            <div className="hidden md:block">
              <DataTable
                label="Price book"
                caption="Immutable price facts by geography tier and usage tier. A price the founder has not ruled stays Not priced."
                columns={[
                  { key: "campaign", label: "Campaign" },
                  { key: "tier", label: "Geography tier" },
                  { key: "usage", label: "Usage tier", numeric: true },
                  { key: "currency", label: "Currency" },
                  { key: "price", label: "Price", numeric: true },
                  { key: "status", label: "Status" },
                  { key: "created", label: "Created" },
                ]}
                rows={priceRows}
                empty={
                  <EmptyState
                    title="No price book rows yet"
                    body="Campaign and family prices appear here once operators record them."
                  />
                }
              />
            </div>
            <MobileRows rows={priceMobileRows} label="Price book mobile view" className="md:hidden" />
          </div>
        )}
      </section>
    </div>
  );
}
